/**
 * @module app.um
 */

import { autoinject, bindable } from 'aurelia-framework';
import { ComponentBase, SortDirection, Sorting } from 'app/shared/lib/essette-site';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { apiCall } from 'app/shared/lib/essette-api';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { AuthService } from 'app/um/services/auth-service';
import * as UM from 'app/um/models/essette-api';

@autoinject()
export class AuthQueueHistory extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable({ changeHandler: 'reload' })
    protected authId: string | null = null;

    protected authQueueHistory: UM.AuthQueueHistory[] = [];

    private sorting = new Sorting();
    protected resultCount = 0;
    protected pageSize = 10;

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: AuthService,
        ...rest: any[]) {
        super(...rest);

        this.sorting.entries = [
            {
                reference: 'metadata.started.timestamp',
                direction: SortDirection.Descending
            }
        ];
    }

    protected bind(): void {
        this.reload();
    }

    ////****************** Events ***************************************************////
    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting: sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        this.reload();
    }

    ////****************** Functions ************************************************////
    protected async reload(): Promise<void> {
        await this.loadAuthQueueHistory();
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    protected async loadAuthQueueHistory(): Promise<void> {
        if(!this.authId) return;

        const options: Partial<IQueryLoadOptions> = {
            top: this.pageSize,
            page: this.$pager?.page || 0,
            sorting: this.sorting
        };

        const includes = [
            'queue',
            'metadata.started.byUser',
            'metadata.ended.byUser'
        ];

        try {
            this.state.beginLoad();

            const response = await this.service.loadQueueHistoryByAuth(this.authId, options, includes);
            this.authQueueHistory = response.results;
            this.resultCount = response.count || 0;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }
}
